import { z } from 'zod'
import {
  DOC_TYPES,
  MAX_CASE_DETAILS_LENGTH,
  MAX_LOT_NUMBER_LENGTH,
  type CaseRequestType,
  type CreateCasePayload,
  type DocType,
} from '@/lib/api-types'
import { zodFieldErrors, type FieldErrors } from '@/lib/zod-errors'

/**
 * Client-side mirror of the API's create-case zod schema (greenline-hub-api
 * routes/client/cases.ts), per form type. The create page validates the built
 * payload against these before sending, so a form that clears validation
 * produces a payload the server accepts as-is (the credit application-schema
 * idiom — no react-hook-form).
 */

const dateString = z.string().regex(/^\d{4}-\d{2}-\d{2}$/)

const commonSchema = z.object({
  onBehalfOfBrand: z.string().trim().max(255).optional(),
  externalReference: z.string().trim().max(100).optional(),
  participants: z.array(z.string()).optional(),
})

export const documentRequestSchema = commonSchema.extend({
  type: z.literal('document_request'),
  productId: z.string().min(1),
  lotNumber: z.string().trim().max(MAX_LOT_NUMBER_LENGTH).optional(),
  docTypes: z.array(z.enum(DOC_TYPES)).min(1),
  details: z.string().trim().max(MAX_CASE_DETAILS_LENGTH).optional(),
})

export const questionnaireSchema = commonSchema.extend({
  type: z.literal('questionnaire'),
  description: z.string().trim().min(1).max(4000),
  dueDate: dateString.optional(),
})

export const qualityIssueSchema = commonSchema.extend({
  type: z.literal('quality_issue'),
  productId: z.string().min(1),
  lotNumber: z.string().trim().min(1).max(MAX_LOT_NUMBER_LENGTH),
  description: z.string().trim().min(1).max(4000),
})

export const questionSchema = commonSchema.extend({
  type: z.enum(['technical', 'regulatory', 'other']),
  subject: z.string().trim().min(1).max(255),
  description: z.string().trim().min(1).max(4000),
  productId: z.string().min(1).optional(),
})

// ---------------------------------------------------------------------------
// Form state (string-typed inputs) → API payload
// ---------------------------------------------------------------------------

/** The product the picker captured — id + name only, never pricing. */
export interface PickedProduct {
  id: string
  name: string
}

export interface RequestFormState {
  product: PickedProduct | null
  lotNumber: string
  docTypes: DocType[]
  /** document_request free-text details. */
  details: string
  /** Shared body field for questionnaire / quality_issue / question types. */
  description: string
  /** questionnaire only — YYYY-MM-DD. */
  dueDate: string
  /** question types only. */
  subject: string
  onBehalfOfBrand: string
  externalReference: string
  /** Colleague contact ids to copy in. */
  participants: string[]
}

export function emptyRequestForm(): RequestFormState {
  return {
    product: null,
    lotNumber: '',
    docTypes: [],
    details: '',
    description: '',
    dueDate: '',
    subject: '',
    onBehalfOfBrand: '',
    externalReference: '',
    participants: [],
  }
}

/** '' → undefined so optional fields are OMITTED from the payload, not sent blank. */
function opt(value: string): string | undefined {
  const trimmed = value.trim()
  return trimmed === '' ? undefined : trimmed
}

/** The exact `payload` JSON — undefined optionals drop out of the JSON entirely. */
export function buildCasePayload(type: CaseRequestType, form: RequestFormState): CreateCasePayload {
  const common = {
    onBehalfOfBrand: opt(form.onBehalfOfBrand),
    externalReference: opt(form.externalReference),
    participants: form.participants.length > 0 ? form.participants : undefined,
  }
  switch (type) {
    case 'document_request':
      return {
        type,
        productId: form.product?.id ?? '',
        lotNumber: opt(form.lotNumber),
        docTypes: form.docTypes,
        details: opt(form.details),
        ...common,
      }
    case 'questionnaire':
      return {
        type,
        description: form.description.trim(),
        dueDate: opt(form.dueDate),
        ...common,
      }
    case 'quality_issue':
      return {
        type,
        productId: form.product?.id ?? '',
        lotNumber: form.lotNumber.trim(),
        description: form.description.trim(),
        ...common,
      }
    case 'technical':
    case 'regulatory':
    case 'other':
      return {
        type,
        subject: form.subject.trim(),
        description: form.description.trim(),
        productId: form.product?.id,
        ...common,
      }
  }
}

const SCHEMAS: Record<CaseRequestType, z.ZodType> = {
  document_request: documentRequestSchema,
  questionnaire: questionnaireSchema,
  quality_issue: qualityIssueSchema,
  technical: questionSchema,
  regulatory: questionSchema,
  other: questionSchema,
}

/**
 * Validates the built payload for a form type. Empty result = send. Keys are
 * payload field names (`productId`, `docTypes`, `description`, …) so the page
 * can wire aria-invalid + FieldError per input.
 */
export function validateForType(type: CaseRequestType, form: RequestFormState): FieldErrors {
  return zodFieldErrors(SCHEMAS[type].safeParse(buildCasePayload(type, form)))
}
